/**
 * FeatureFlagsContext — web port of `apps/WunderGuide/hooks/useFeatureFlags.ts`.
 *
 * Behavior mirrors RN: fetch resolved flags on mount, refresh on document
 * `visibilitychange` (the web analog of AppState 'active'), expose
 * `isEnabled(key, default)`. Flags drive nav-tab visibility, the interpreter
 * chip, etc. — same gating logic as RN Dashboard.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { featureFlagsApi, ResolvedFlags } from '../api/featureFlagsApi';

interface FeatureFlagsContextValue {
  flags: ResolvedFlags;
  isEnabled: (key: string, defaultValue?: boolean) => boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<ResolvedFlags>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);

  const fetchFlags = useCallback(async () => {
    try {
      setError(null);
      const resolved = await featureFlagsApi.getFlags();
      if (isMounted.current) {
        setFlags(resolved);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'Failed to load flags');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  const isEnabled = useCallback(
    (key: string, defaultValue: boolean = false): boolean =>
      key in flags ? flags[key] : defaultValue,
    [flags],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Refresh when tab returns to foreground (web equivalent of AppState 'active').
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchFlags();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider
      value={{ flags, isEnabled, loading, error, refresh: fetchFlags }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return ctx;
}

export default useFeatureFlags;
